import type {BusinessRFI} from '@/types';
import {BusinessRFISource, BusinessRFIStatus} from '@/types';

/** Map an RFI status to a Badge colorScheme (see components/common/Badge). */
export const rfiStatusColorScheme = (status: BusinessRFIStatus): string => {
  switch (status) {
    // Not yet a real request: a rail parked a payment and nobody has named the
    // documents. Neutral grey so it does not read as awaiting the business.
    case BusinessRFIStatus.Draft:
      return 'refunded';
    case BusinessRFIStatus.Resolved:
      return 'successful';
    case BusinessRFIStatus.ReuploadRequested:
      return 'failed';
    case BusinessRFIStatus.Submitted:
      return 'blue';
    case BusinessRFIStatus.Requested:
    case BusinessRFIStatus.Reuploaded:
    default:
      return 'pending';
  }
};

/** Options for the status filter Select ("" = all). */
export const RFI_STATUS_FILTER_OPTIONS: {key: string; value: string}[] = [
  {value: '', key: 'All statuses'},
  {value: BusinessRFIStatus.Draft, key: 'Draft (needs documents named)'},
  {value: BusinessRFIStatus.Requested, key: 'Requested'},
  {value: BusinessRFIStatus.Submitted, key: 'Submitted'},
  {value: BusinessRFIStatus.ReuploadRequested, key: 'Reupload requested'},
  {value: BusinessRFIStatus.Reuploaded, key: 'Reuploaded'},
  {value: BusinessRFIStatus.Resolved, key: 'Resolved'},
];

/** Options for the source filter Select ("" = all). */
export const RFI_SOURCE_FILTER_OPTIONS: {key: string; value: string}[] = [
  {value: '', key: 'All sources'},
  {value: BusinessRFISource.Processor, key: 'Raised by a processor'},
  {value: BusinessRFISource.Admin, key: 'Raised by an admin'},
];

/**
 * Requested document types the business still owes: not uploaded yet, OR flagged
 * for re-upload (the existing file is stale and must be replaced). Mirrors the
 * server's computeOutstanding — this previously ignored reuploadRequested, which
 * under-reported the count after a re-upload request because the admin type did
 * not carry the field.
 */
export const getOutstandingDocuments = (rfi: BusinessRFI): string[] => {
  const uploaded = new Set(Object.keys(rfi.documents || {}));
  const reupload = new Set(rfi.reuploadRequested ?? []);
  return (rfi.requestedDocuments || []).filter(
    (doc) => !uploaded.has(doc) || reupload.has(doc),
  );
};

/**
 * Requested document types that already have an uploaded file — i.e. the docs an
 * admin can ask the business to redo. Falls back to all requested documents when
 * nothing has been uploaded yet (so re-upload is never an empty picker).
 */
export const getReuploadableDocuments = (rfi: BusinessRFI): string[] => {
  const requested = rfi.requestedDocuments || [];
  const uploaded = requested.filter((doc) => !!rfi.documents?.[doc]);
  return uploaded.length ? uploaded : requested;
};
